import { ReactNode } from 'react';
import {
	ArrayField,
	DateField,
	DeleteButton,
	EmailField,
	FunctionField,
	ReferenceField,
	SingleFieldList,
	TextField,
	UrlField,
	useRecordContext,
	useTranslate,
	WithRecord
} from 'react-admin';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { Edit, Email, LinkedIn, Phone, Visibility } from '@mui/icons-material';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { OBJECTS_TYPES } from '../../../constants';

export const BuildingAside = ({
	link = 'edit'
}: {
	link?: 'edit' | 'show';
}) => {
	const translate = useTranslate();
	const location = useLocation();
	const record = useRecordContext();
	if (!record) return null;

	return (
		<Box ml={4} width={250} minWidth={250}>
			<Box mb={2} ml="-5px">
				<Button
					component={Link}
					to={`/objects/${OBJECTS_TYPES.BUILDINGS}/${link}/${record.id}`}
					variant="outlined"
					startIcon={link === 'edit' ? <Edit /> : <Visibility />}
				>
					{link === 'edit'
						? translate('ra.action.edit')
						: translate('ra.action.show')}
				</Button>
			</Box>
			<Typography variant="subtitle2">Personal info</Typography>
			<Divider sx={{ mb: 2 }} />
			<ArrayField source="email_jsonb">
				<SingleFieldList linkType={false} gap={0} direction="column">
					<PersonalInfoRow
						icon={<Email color="disabled" fontSize="small" />}
						primary={<EmailField source="email" />}
						showType
					/>
				</SingleFieldList>
			</ArrayField>
			{record.has_newsletter && (
				<Typography variant="body2" color="textSecondary" pl={3.5}>
					Subscribed to newsletter
				</Typography>
			)}
			{record.linkedin_url && (
				<PersonalInfoRow
					icon={<LinkedIn color="disabled" fontSize="small" />}
					primary={
						<UrlField
							source="linkedin_url"
							content="LinkedIn profile"
							target="_blank"
							rel="noopener"
						/>
					}
				/>
			)}
			<ArrayField source="phone_jsonb">
				<SingleFieldList linkType={false} gap={0} direction="column">
					<PersonalInfoRow
						icon={<Phone color="disabled" fontSize="small" />}
						primary={<TextField source="number" />}
						showType
					/>
				</SingleFieldList>
			</ArrayField>
			<Box mt={1} mb={3}>
				<Typography component="span" variant="body2" color="textSecondary">
					{translate('form.fields.createdAt')}
				</Typography>
				<DateField
					source="created_at"
					showTime={true}
					locales="uk-UA"
					color="textSecondary"
				/>
				<br />
				<Typography component="span" variant="body2" color="textSecondary">
					{translate('form.fields.updatedAt')}
				</Typography>
				<DateField
					source="updated_at"
					showTime={true}
					locales="uk-UA"
					color="textSecondary"
				/>
				<br />
				<Typography component="span" variant="body2" color="textSecondary">
					{translate('form.fields.followedBy')}
				</Typography>
				<ReferenceField source="sales_id" reference="sales">
					<FunctionField
						source="last_name"
						render={record => `${record.first_name} ${record.last_name}`}
					/>
				</ReferenceField>
			</Box>
			<DeleteButton redirect={location.state?.from || undefined} />
		</Box>
	);
};

const PersonalInfoRow = ({
	icon,
	primary,
	showType
}: {
	icon: ReactNode;
	primary: ReactNode;
	showType?: boolean;
}) => (
	<Stack direction="row" alignItems="center" gap={1} minHeight={24}>
		{icon}
		<Box display="flex" flexWrap="wrap" columnGap={0.5} rowGap={0}>
			{primary}
			{showType ? (
				<WithRecord
					render={row =>
						row.type !== 'Other' && (
							<TextField source="type" color="textSecondary" />
						)
					}
				/>
			) : null}
		</Box>
	</Stack>
);
